import { ChangeEvent, useMemo, useRef } from 'react';
import { TFunction } from 'i18next';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Capacitor } from '@capacitor/core';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { enqueueSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import { WildfireFormMap } from 'app/pages/CreateEditWildfire/WildfireForm/components/WildfireFormMap';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { useModerationReports } from 'components/IncidentsMap/SearchDrawer/hooks/useModerationReports';
import { MapFormLayout } from 'components/MapFormLayout';
import { LatLngForm } from 'components/MediaForm/LatLngForm';
import { MediaMapForm } from 'components/MediaForm/MediaMapForm';
import {
  RichTextEditor,
  RichTextEditorRefContent,
} from 'components/RichTextEditor';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { PhotoMarkersLayer } from 'features/mapping/components/layers/PhotoMarkersLayer';
import { Map } from 'features/mapping/components/Map';
import { isRingReport } from 'features/reports/reports.utils';
import { Report } from 'features/reports/report.types';
import { UseReport } from 'features/reports/hooks/useReport/useReport.types';
import { RelatedFlooding } from 'features/flooding/flooding.types';
import { Hazard } from 'features/hazards/hazard.types';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { getTimeAgo, getDateFormatted } from 'shared/dates';
import { MapProvider } from 'shared/map-exports';
import { useCacheState } from 'state/useCacheState';
import { useMapState } from 'state/useMapState';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import {
  ModerateSubmissionFormValues,
  SubmissionStatus,
  SelectOptions,
  SubmissionPostMethod,
} from './ModerateSubmission.types';
import { getValidationSchema } from './ModerateSubmission.validation';

const getStatusOptions = (isRingSource: boolean): SelectOptions => [
  {
    value: 'approve',
    transKey: 'moderateSubmission.inputs.status.options.approve',
    disabled: false,
  },
  {
    value: 'reject',
    transKey: 'moderateSubmission.inputs.status.options.reject',
    disabled: false,
  },
  {
    value: 'rejectDevice',
    transKey: 'moderateSubmission.inputs.status.options.rejectDevice',
    disabled: !isRingSource,
  },
];

const getPostMethodOptions = (): SelectOptions => [
  {
    value: 'mapOnly',
    transKey: 'moderateSubmission.inputs.postMethod.options.mapOnly',
    disabled: false,
  },
  {
    value: 'mapAndSilentUpdate',
    transKey: 'moderateSubmission.inputs.postMethod.options.mapAndSilentUpdate',
    disabled: false,
  },
];

const getSubmitButtonProps = (
  t: TFunction,
  status: SubmissionStatus,
  postMethod: SubmissionPostMethod,
  forceDisabled: boolean,
): {
  label: string;
  color: 'primary' | 'error';
  disabled: boolean;
} => {
  switch (status) {
    case 'approve':
      return {
        label: t(
          postMethod === 'mapOnly'
            ? 'moderateSubmission.buttons.approveAndPost'
            : 'moderateSubmission.buttons.approveAndUpdate',
        ),
        color: 'primary',
        disabled: forceDisabled,
      };
    case 'reject':
      return {
        label: t('moderateSubmission.buttons.reject'),
        color: 'error',
        disabled: false,
      };
    case 'rejectDevice':
      return {
        label: t('moderateSubmission.buttons.rejectDevice'),
        color: 'error',
        disabled: false,
      };
    default:
      return {
        label: t('moderateSubmission.buttons.save'),
        color: 'primary',
        disabled: true,
      };
  }
};

const createOptions = (options: SelectOptions, t: TFunction): JSX.Element[] =>
  options.map(({ disabled, transKey, value }) => (
    <MenuItem disabled={disabled} key={value} value={value}>
      {t(transKey)}
    </MenuItem>
  ));

type ModerateSubmissionFormProps = {
  report: Report;
  onDeleteReport: UseReport['onDeleteReport'];
  onUpdateReport: UseReport['onUpdateReport'];
};

export const useStyles = makeStyles()(() => ({
  form: {
    paddingBottom: 'max(env(safe-area-inset-bottom), 24px)',
    minHeight: '100%',
    width: '100%',
  },
  singleLineEditor: {
    minHeight: 'auto',
  },
}));

export const ModerateSubmissionForm = (
  props: ModerateSubmissionFormProps,
): JSX.Element => {
  const { report, onDeleteReport, onUpdateReport } = props;
  const { setCacheState } = useCacheState();
  const { incidentMapState, updateIncidentMapState } = useMapState();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up(1024), {
    defaultMatches: !Capacitor.isNativePlatform(),
  });
  const isRingSource = isRingReport(report);
  const descriptionEditorRef = useRef<RichTextEditorRefContent>(null);
  const location = useLocation<{ returnOnSuccess?: boolean }>();
  const { reports } = useModerationReports({ forceEnabled: true });
  const otherReports = useMemo(
    () => reports.filter((r) => r.id !== report.id),
    [reports, report.id],
  );

  // If the report is not awaiting approval, they are editing so certain things will be different
  const isEditing = report.status !== 'llm_reviewed';

  const mapCenterFromReportMedia =
    report.media?.[0]?.lat && report.media?.[0]?.lng
      ? { lat: report.media?.[0]?.lat, lng: report.media?.[0]?.lng }
      : undefined;

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const defaultDescription = isEditing
    ? report.message
    : t(
        `moderateSubmission.inputs.description.placeholder.${isRingSource ? 'ring' : 'wd'}`,
      );

  const initialValues: ModerateSubmissionFormValues = {
    azimuth: report.media[0]?.az ?? 0,
    description: defaultDescription,
    geoEventId: report.geoEventId,
    latitude: String(report.media[0]?.lat ?? ''),
    longitude: String(report.media[0]?.lng ?? ''),
    status: isEditing ? 'approve' : '',
    postMethod:
      isEditing && report.showInReportsView ? 'mapAndSilentUpdate' : 'mapOnly',
  };

  const formMethods = useForm<ModerateSubmissionFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    values: initialValues,
    mode: 'onBlur',
  });

  const { control, formState, handleSubmit, setValue, watch } = formMethods;

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const status = watch('status');
  const postMethod = watch('postMethod');
  const geoEventId = watch('geoEventId');
  const description = watch('description');
  const formLat = watch('latitude');
  const formLng = watch('longitude');
  const formAz = watch('azimuth');

  const isStatusApproved = status === 'approve';

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<RelatedWildfire | RelatedFlooding | Hazard>({
      enabled: status === 'approve',
      latLng: mapCenterFromReportMedia,
      value: null,
      geoEventTypes: ['wildfire', 'flooding', 'hazard'],
      multiple: false,
      reporterManagedOnly: true,
    });

  // Find the currently selected geo event from options
  const selectedGeoEvent =
    geoEventAutocompleteProps.options.find(
      (geoEvent) => geoEvent.id === geoEventId,
    ) ?? null;

  const forceSubmittedDisabled = !selectedGeoEvent || !description;
  const submitButtonProps = getSubmitButtonProps(
    t,
    status,
    postMethod,
    forceSubmittedDisabled,
  );

  const handleLatLngChange = (e: ChangeEvent<HTMLInputElement>): void => {
    // Allow users to paste `lat, lng` as a string and have it automagically be parsed
    const { name, value } = e.target;

    const [lat, lng] = value.split(/, ?/);
    if (!Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
      setValue('latitude', lat, { shouldDirty: true });
      setValue('longitude', lng, { shouldDirty: true });
    } else {
      setValue(name as 'latitude' | 'longitude', value, { shouldDirty: true });
    }
  };

  const handlePostMethodChange = (newValue: string): void => {
    if (
      newValue === 'mapAndSilentUpdate' &&
      description === defaultDescription
    ) {
      setValue('description', '');
      descriptionEditorRef.current?.editor?.commands.setContent('');
    }
  };

  const handleSubmitForm: SubmitHandler<ModerateSubmissionFormValues> = async (
    values,
  ) => {
    if (values.status === 'reject' || values.status === 'rejectDevice') {
      //  Some form of rejected
      try {
        await onDeleteReport(values.status === 'rejectDevice');
        enqueueSnackbar(t('moderateSubmission.success'), {
          variant: 'success',
        });
        unblockedNavigate(-1);
      } catch (error) {
        enqueueSnackbar(t('moderateSubmission.error'), { variant: 'error' });
      }
      return;
    }

    //  They have approved
    const updateData: Partial<Report> = {
      geoEventId: values.geoEventId,
      message: values.description,
      showInReportsView: values.postMethod === 'mapAndSilentUpdate',
    };

    //  Only allow lat/lng/az to be set for non-Ring reports
    if (!isRingSource) {
      updateData.lat = Number(values.latitude);
      updateData.lng = Number(values.longitude);
      updateData.media = [
        {
          ...report.media[0],
          lat: Number(values.latitude),
          lng: Number(values.longitude),
          az: values.azimuth,
        },
      ];
    }

    const successLink: string | number =
      location.state?.returnOnSuccess || isEditing
        ? -1
        : `/i/${values.geoEventId}`;

    try {
      setCacheState(null);
      await onUpdateReport(updateData);
      enqueueSnackbar(t('moderateSubmission.success'), { variant: 'success' });
      unblockedNavigate(successLink);
    } catch (error) {
      enqueueSnackbar(t('moderateSubmission.error'), { variant: 'error' });
    }
  };

  const reportWithCurrentGeoSettings = useMemo(() => {
    const cloned = structuredClone(report);
    cloned.media = [
      {
        ...cloned.media[0],
        lat: Number(formLat),
        lng: Number(formLng),
        az: formAz,
      },
    ];
    return cloned;
  }, [report, formLat, formLng, formAz]);

  return (
    <MapProvider>
      <FormProvider {...formMethods}>
        <MapFormLayout>
          <MapFormLayout.MapContent>
            <WildfireFormMap
              center={mapCenterFromReportMedia ?? incidentMapState.center}
              zoom={incidentMapState.zoom}
              onViewportChange={updateIncidentMapState}
              isEvacZonesLayerVisible={false}
              evacZones={[]}
              pendingEvacZones={[]}
              showHazards
            >
              {mapCenterFromReportMedia && (
                <>
                  <PhotoMarkersLayer
                    reports={otherReports}
                    hoveredReportId={null}
                  />
                  <Map.ReportMediaMarkers
                    isFadable={false}
                    reports={reportWithCurrentGeoSettings}
                    showObfuscation={isRingSource}
                  />
                </>
              )}
            </WildfireFormMap>
          </MapFormLayout.MapContent>

          <MapFormLayout.FormContent>
            <FormWithoutEnter
              noValidate
              className={classes.form}
              onSubmit={handleSubmit(handleSubmitForm)}
            >
              <Stack spacing={2}>
                <Typography variant="h3">
                  {t('moderateSubmission.sections.submission')}
                </Typography>
                <TextField
                  id="field-source"
                  label={t('moderateSubmission.inputs.source.label')}
                  fullWidth
                  disabled
                  value={t(
                    `moderateSubmission.inputs.source.${isRingSource ? 'ring' : 'wd'}`,
                  )}
                />
                <TextField
                  id="field-submitted"
                  label={t('moderateSubmission.inputs.submitted.label')}
                  fullWidth
                  disabled
                  value={`${getTimeAgo(report.dateCreated)}, ${getDateFormatted(report.dateCreated, { includeYear: true, includeTimeZone: true })}`}
                />
                <TextField
                  id="field-user-description"
                  label={t('moderateSubmission.inputs.userDescription.label')}
                  fullWidth
                  disabled
                  multiline
                  minRows={3}
                  value={report.message}
                />
                <Typography variant="h3">
                  {t('moderateSubmission.sections.photo')}
                </Typography>
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState }) => {
                    const { ref, onChange, ...muiFieldProps } = field;
                    return (
                      <TextField
                        select
                        id="field-control-status"
                        label={t('moderateSubmission.inputs.status.label')}
                        fullWidth
                        {...muiFieldProps}
                        value={muiFieldProps.value || ''}
                        inputRef={ref}
                        onChange={(e) => {
                          const newValue = e.target.value as SubmissionStatus;
                          onChange(newValue);
                        }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        required
                        disabled={formState.isSubmitting || isEditing}
                      >
                        {createOptions(getStatusOptions(isRingSource), t)}
                      </TextField>
                    );
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isLargeMediaQuery ? 'row' : 'column',
                    gap: 4,
                  }}
                >
                  <Box
                    component="img"
                    src={report.media[0]?.url}
                    alt="Submitted photo"
                    sx={{
                      width: isLargeMediaQuery ? '50%' : '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />

                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <LatLngForm
                        fieldName="latitude"
                        disabled={isRingSource || !isStatusApproved}
                        onChange={handleLatLngChange}
                      />
                      <LatLngForm
                        fieldName="longitude"
                        disabled={isRingSource || !isStatusApproved}
                        onChange={handleLatLngChange}
                      />
                    </Box>

                    <MediaMapForm
                      latFieldName="latitude"
                      lngFieldName="longitude"
                      azFieldName="azimuth"
                      allowAzimuth
                      showMap={false}
                      disabled={isRingSource || !isStatusApproved}
                    />
                  </Stack>
                </Box>
                {status === 'approve' && (
                  <>
                    <Controller
                      name="postMethod"
                      control={control}
                      render={({ field }) => {
                        const { ref, onChange, ...muiFieldProps } = field;
                        return (
                          <TextField
                            select
                            id="field-post-method"
                            label={t(
                              'moderateSubmission.inputs.postMethod.label',
                            )}
                            fullWidth
                            {...muiFieldProps}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              onChange(newValue);
                              handlePostMethodChange(newValue);
                            }}
                            inputRef={ref}
                            disabled={formState.isSubmitting}
                            required
                          >
                            {createOptions(getPostMethodOptions(), t)}
                          </TextField>
                        );
                      }}
                    />

                    <Controller
                      name="geoEventId"
                      control={control}
                      render={({ field, fieldState }) => {
                        const hasError = Boolean(fieldState.error);
                        return (
                          <FormControl fullWidth required>
                            <AutocompleteWithBackdrop
                              {...geoEventAutocompleteProps}
                              disabled={formState.isSubmitting || isEditing}
                              id="field-incident"
                              value={selectedGeoEvent}
                              onChange={(_e, value) => {
                                field.onChange(
                                  Array.isArray(value)
                                    ? null
                                    : (value?.id ?? null),
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name={field.name}
                                  inputRef={field.ref}
                                  variant="outlined"
                                  label={t(
                                    'moderateSubmission.inputs.incident.label',
                                  )}
                                  error={hasError}
                                  helperText={fieldState.error?.message}
                                  required={isStatusApproved}
                                />
                              )}
                            />
                          </FormControl>
                        );
                      }}
                    />

                    <Controller
                      name="description"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <RichTextEditor
                            ref={descriptionEditorRef}
                            id="field-description"
                            initialValue={initialValues.description}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            label={t(
                              'moderateSubmission.inputs.description.label',
                            )}
                            required
                            error={Boolean(fieldState.error)}
                            editorClassName={classes.singleLineEditor}
                          />
                          <FormHelperText
                            error={Boolean(fieldState.error)}
                            sx={{ mx: 1.75 }}
                          >
                            {fieldState.error?.message}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </>
                )}
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  color={submitButtonProps.color}
                  disabled={
                    submitButtonProps.disabled || formState.isSubmitting
                  }
                >
                  {submitButtonProps.label}
                </Button>
              </Stack>
            </FormWithoutEnter>
          </MapFormLayout.FormContent>
        </MapFormLayout>
      </FormProvider>
    </MapProvider>
  );
};
